import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Good Buy cart drawer upsells and shipping protection toggle.
 * Uses the theme's own cart events so the drawer, cart bubble and product forms all refresh.
 */
if (!window.__gbcCartInit) {
  window.__gbcCartInit = true;

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

  const mutateCart = async (source, { action, url, body, lines }) => {
    const deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      const failed = Boolean(data.status) || Boolean(data.errors);

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: failed ? undefined : data.sections,
          source: 'gbc-cart-upsell',
          didError: failed,
        },
      });
      return !failed;
    } catch (error) {
      console.error('[gbc-cart-upsell]', error);
      deferred.reject(error);
      return false;
    }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-gbc-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const card = button.closest('[data-gbc-card]');
    const input = card?.querySelector('[data-gbc-variant]');
    const variantId = input && 'value' in input ? String(input.value) : '';
    if (!variantId) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const ok = await mutateCart(button, {
      action: 'add',
      url: Theme.routes.cart_add_url,
      body: { items: [{ id: Number(variantId), quantity: 1 }] },
      lines: [{ merchandiseId: variantId, quantity: 1 }],
    });

    if (!ok && button.isConnected) {
      button.disabled = false;
      button.textContent = label;
    }
  });

  document.addEventListener('change', async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('select[data-gbc-variant]') && target instanceof HTMLSelectElement) {
      const option = target.selectedOptions[0];
      const card = target.closest('[data-gbc-card]');
      const price = card?.querySelector('[data-gbc-price]');
      const compare = card?.querySelector('[data-gbc-compare]');
      if (option && price) price.textContent = option.dataset.price || '';
      if (option && compare instanceof HTMLElement) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
      return;
    }

    if (target.matches('input[data-gbc-protect]') && target instanceof HTMLInputElement) {
      target.disabled = true;
      const { variant, key } = target.dataset;

      const ok = target.checked
        ? await mutateCart(target, {
            action: 'add',
            url: Theme.routes.cart_add_url,
            body: { items: [{ id: Number(variant), quantity: 1 }] },
            lines: [{ merchandiseId: String(variant), quantity: 1 }],
          })
        : await mutateCart(target, {
            action: 'remove',
            url: Theme.routes.cart_change_url,
            body: { id: key, quantity: 0 },
            lines: [{ id: key, quantity: 0 }],
          });

      if (target.isConnected) {
        if (!ok) target.checked = !target.checked;
        target.disabled = false;
      }
    }
  });
}

/**
 * "Your cart is reserved" countdown. One shared end time per browser tab session,
 * so re-renders of the drawer don't reset it. Quietly restarts at 5:00 once it runs out.
 */
if (!window.__gbcTimerInit) {
  window.__gbcTimerInit = true;

  const KEY = 'gbcReserveEnd';
  const DURATION = 5 * 60 * 1000;

  const getEnd = () => {
    let end = 0;
    try {
      end = Number(sessionStorage.getItem(KEY)) || 0;
    } catch (e) {}
    if (!end || end <= Date.now()) {
      end = Date.now() + DURATION;
      try {
        sessionStorage.setItem(KEY, String(end));
      } catch (e) {}
    }
    return end;
  };

  const tick = () => {
    const els = document.querySelectorAll('[data-gbc-timer]');
    if (!els.length) return;
    const left = Math.max(0, getEnd() - Date.now());
    const mins = Math.floor(left / 60000);
    const secs = Math.floor((left % 60000) / 1000);
    const text = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    els.forEach((el) => {
      if (el.textContent !== text) el.textContent = text;
    });
  };

  tick();
  setInterval(tick, 250);
}